import { motion, animate, useMotionValue, useTransform } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const trustPoints = [
  { icon: 'ri-money-dollar-circle-line', title: '바가지 없는\n투명 견적' },
  { icon: 'ri-map-pin-line', title: '전지역\n직영 운행' },
  { icon: 'ri-user-heart-line', title: '10년 이상\n지역 신뢰' },
];

const lines = ['바가지 없는', '믿을 수 있는 이사업체'];

function TypingTitle() {
  const [displayedLines, setDisplayedLines] = useState<string[]>(['', '']);
  const [done, setDone] = useState(false);
  const fontSize = useMotionValue(18);
  const fontSizeStr = useTransform(fontSize, (v) => `${v}px`);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    let cancelled = false;
    const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

    const run = async () => {
      await delay(400);
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        for (let ci = 0; ci <= line.length; ci++) {
          if (cancelled) return;
          setDisplayedLines((prev) => {
            const next = [...prev];
            next[li] = line.slice(0, ci);
            return next;
          });
          await delay(80);
        }
        if (li < lines.length - 1) await delay(120);
      }
      setDone(true);
    };

    run();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const totalChars = displayedLines.reduce((s, l) => s + l.length, 0);
    const maxChars = lines.reduce((s, l) => s + l.length, 0);
    const progress = maxChars > 0 ? totalChars / maxChars : 0;
    const target = 18 + progress * (28 - 18);
    animate(fontSize, target, { duration: 0.15, ease: 'easeOut' });
  }, [displayedLines, fontSize]);

  return (
    <motion.h3
      className="text-white font-bold mb-3 leading-snug"
      style={{ fontSize: fontSizeStr }}
    >
      {displayedLines[0]}
      <br />
      {displayedLines[1]}
      {!done && (
        <motion.span
          className="inline-block w-0.5 h-5 bg-white align-middle ml-0.5"
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity, repeatType: 'reverse' }}
        />
      )}
    </motion.h3>
  );
}

export default function QuoteForm() {
  return (
    <motion.div
      className="bg-teal-600 rounded-lg p-8"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={containerVariants}
    >
      {/* 신뢰 뱃지 */}
      <motion.div className="flex items-center gap-2 mb-4" variants={itemVariants}>
        <motion.div
          className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full"
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
        >
          <i className="ri-shield-check-line text-white text-lg"></i>
        </motion.div>
        <span className="text-white/90 text-sm font-medium">지역 직영 업체</span>
      </motion.div>

      {/* 타이핑 타이틀 */}
      <TypingTitle />

      {/* 설명 */}
      <motion.p
        className="text-white/85 text-sm leading-relaxed mb-6"
        variants={itemVariants}
      >
        저희는 <strong className="text-white">강원도 직영</strong>으로 운영합니다.<br />
        중간 플랫폼 없이 직접 운영하기 때문에 <strong className="text-white">불필요한 수수료와 바가지가 없습니다.</strong><br />
        10년 이상 지역에서 쌓아온 신뢰로 정직하게 견적을 드립니다.
      </motion.p>

      {/* 신뢰 포인트 3가지 */}
      <motion.div className="grid grid-cols-3 gap-3 mb-8" variants={itemVariants}>
        {trustPoints.map((point, index) => (
          <motion.div
            key={index}
            className="bg-white/15 rounded-lg p-3 text-center cursor-default"
            whileHover={{ scale: 1.06, backgroundColor: 'rgba(255,255,255,0.25)' }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
              <i className={`${point.icon} text-white text-xl`}></i>
            </div>
            <p className="text-white text-xs font-semibold whitespace-pre-line">{point.title}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* 버튼들 */}
      <motion.div className="space-y-2.5" variants={itemVariants}>
        <motion.a
          href="https://talk.naver.com/profile/wfu3m6i"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full bg-gray-900 text-white py-3 rounded-md font-semibold text-sm cursor-pointer whitespace-nowrap"
          whileHover={{ scale: 1.02, backgroundColor: '#1f2937' }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <i className="ri-message-3-line text-base"></i>
          견적신청하기
        </motion.a>

        <motion.a
          href="https://open.kakao.com/o/sduqXZli"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full bg-yellow-400 text-gray-900 py-3 rounded-md font-semibold text-sm cursor-pointer whitespace-nowrap"
          whileHover={{ scale: 1.02, backgroundColor: '#facc15' }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <i className="ri-kakao-talk-fill text-base"></i>
          카카오톡 실시간 상담
        </motion.a>

        <motion.a
          href="tel:0507-1366-2424"
          className="flex items-center justify-center gap-2 w-full bg-white/20 border border-white/40 text-white py-3 rounded-md font-semibold text-sm cursor-pointer whitespace-nowrap"
          whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.3)' }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <motion.i
            className="ri-phone-line text-base"
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 4 }}
          ></motion.i>
          전화 상담 바로 연결
        </motion.a>
      </motion.div>

      <motion.p
        className="text-white/60 text-xs text-center mt-4"
        variants={itemVariants}
      >
        평일 09:00 ~ 18:00 · 공휴일 운영
      </motion.p>
    </motion.div>
  );
}
